<template>
    <CustomModal
        :isOpen="show"
        @close="emit('close')"
        width="600px"
        modalWidth="min(600px, 95%)"
        :fullScreenMobile="true"
        :zIndex="10000"
    >
        <template #content>
            <div
                class="upgrade-modal-bg flex flex-col items-center"
                :style="{
                    margin: '-24px',
                    padding: isMobile
                        ? '36px 24px 24px 24px'
                        : '36px 64px 24px 64px',
                }"
            >
                <Icon icon="10xTravel" class="mb-6" />

                <h3
                    class="text-[24px] font-semibold leading-8 text-slate-800 dark:text-slate-100 text-center self-stretch mb-2"
                >
                    Upgrade to get the full 10xTravel experience
                </h3>

                <p
                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 text-center self-stretch mb-6"
                >
                    {{ description }}
                </p>

                <!-- Toggle + Features wrapper (Figma: flex col, items-center, gap-32, pb-48, self-stretch) -->
                <div
                    class="flex flex-col items-center gap-8 pb-12 self-stretch"
                >
                    <!-- Monthly / Yearly toggle -->
                    <div class="inline-flex items-start rounded bg-slate-100 dark:bg-slate-700">
                        <button
                            @click="billingCycle = 'monthly'"
                            class="flex w-[190px] h-10 px-4 py-2.5 justify-center items-center gap-2 rounded text-sm font-medium leading-5 transition-colors"
                            :class="
                                billingCycle === 'monthly'
                                    ? 'border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]'
                                    : 'text-slate-600 dark:text-slate-300'
                            "
                        >
                            Monthly
                        </button>
                        <button
                            @click="billingCycle = 'yearly'"
                            class="flex w-[190px] h-10 px-4 py-2.5 justify-center items-center gap-2 rounded text-sm font-medium leading-5 whitespace-nowrap transition-colors"
                            :class="
                                billingCycle === 'yearly'
                                    ? 'border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]'
                                    : 'text-slate-600 dark:text-slate-300'
                            "
                        >
                            Yearly
                            <span
                                class="text-[#FD421D] text-sm font-normal leading-5"
                            >
                                2 Months Free
                            </span>
                        </button>
                    </div>

                    <!-- Features list -->
                    <div class="flex flex-col gap-4 self-stretch">
                        <div class="flex items-center gap-3">
                            <Icon
                                icon="dollar-coin"
                                :width="20"
                                :height="20"
                                class="shrink-0 text-[#FD421D]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                Unlimited credit card benefits tracking
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <Icon
                                icon="users"
                                :width="20"
                                :height="20"
                                class="shrink-0 text-[#FD421D]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                Members-only travel community
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <Icon
                                icon="credit-card"
                                :width="20"
                                :height="20"
                                class="shrink-0 text-[#FD421D]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                Best cards by category
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <Icon
                                icon="email-forward"
                                :width="20"
                                :height="20"
                                class="shrink-0 text-[#FD421D]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                Import redemptions from email
                            </span>
                        </div>
                        <div class="flex items-center gap-3">
                            <Icon
                                icon="bell"
                                :svgClass="'w-5 h-5'"
                                class="shrink-0 text-[#FD421D]"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800 dark:text-slate-100"
                            >
                                Award alerts
                            </span>
                        </div>
                        <div
                            v-if="transferBonusAlertsEnabled"
                            class="flex items-center gap-3"
                        >
                            <BenefitIcon
                                type="transfer-bonuses-alt"
                                class="h-5 w-5 shrink-0"
                            />
                            <span
                                class="text-sm font-normal leading-5 text-slate-800"
                            >
                                Transfer bonus alerts
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div
                class="flex flex-col items-center gap-2"
                style="
                    margin-left: -24px;
                    margin-right: -24px;
                    margin-bottom: -24px;
                "
                :style="{
                    padding: isMobile ? '0 24px 24px 24px' : '0 64px 24px 64px',
                }"
            >
                <button
                    @click="handleUpgrade"
                    class="flex px-4 py-2.5 justify-center items-center gap-2 self-stretch rounded bg-[#FD421D] shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-sm font-medium leading-5 text-white hover:bg-[#e53b19] transition-colors"
                >
                    {{
                        billingCycle === 'monthly'
                            ? 'Get 10xTravel+ for $10/month'
                            : 'Get 10xTravel+ for $8.33/month'
                    }}
                </button>
                <button
                    @click="handleLearnMore"
                    class="flex px-4 py-2.5 justify-center items-center gap-2 self-stretch rounded text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                    Learn more
                </button>
            </div>
        </template>
    </CustomModal>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { router } from '@inertiajs/vue3';
import CustomModal from '@/components/ui/modal/custom-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import BenefitIcon from '@/components/ui/icon/BenefitIcon.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const { transferBonusAlertsEnabled } = useFeatureFlags();

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    description: {
        type: String,
        default:
            'This feature is part of the 10xTravel+ plan. Upgrade to unlock it and get full access.',
    },
});

const emit = defineEmits(['close']);

const viewportWidth = ref(0);
const isMobile = computed(
    () => viewportWidth.value > 0 && viewportWidth.value < 640
);

const updateViewport = () => {
    viewportWidth.value = window.innerWidth;
};
onMounted(() => {
    updateViewport();
    window.addEventListener('resize', updateViewport);
});
onUnmounted(() => {
    window.removeEventListener('resize', updateViewport);
});

const billingCycle = ref('monthly');

const handleUpgrade = () => {
    emit('close');
    const billing = billingCycle.value === 'yearly' ? 'yearly' : 'monthly';
    window.open(`/10xtravel-plus?billing=${billing}`, '_blank');
};

const handleLearnMore = () => {
    emit('close');
    window.open('/10xtravel-plus', '_blank');
};
</script>

<style scoped>
.upgrade-modal-bg {
    background: linear-gradient(
        to bottom,
        rgba(253, 66, 29, 0.05) 0%,
        white 40%
    );
}
:where(.dark) .upgrade-modal-bg {
    background: linear-gradient(
        to bottom,
        rgba(253, 66, 29, 0.08) 0%,
        rgb(30, 41, 59) 40%
    );
}
</style>
